import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import {
  combineLatest, merge, Observable, of
} from 'rxjs';
import {
  concatMap, distinctUntilChanged,
  filter, map, take, tap
} from 'rxjs/operators';

import { ALLOW_TEST_VIEW } from 'app/constants/dev-test.data';
import { isDevEnv } from 'app/utils/environment-utils';
import {
  Offer,
  AppProductLine as ProductLine,
  AppCustomLine as CustomLine,
  AppArticle as Article
} from 'graphql/models/extended';
import {
  Customer, Object as Objekt, Account, AccountProfile,
  ContactPerson, Branch, SftpCredentialsResult
} from 'graphql/models/generated';
import {
  CurrentAccountRoles, AccountRole, AccountTypeENUM
} from 'models/account.model';
import { DataItem, DataResourceType } from 'models/data.model';
import {
  ImportJobs, ImportJob, ImportJobStatus, ImportJobType
} from 'models/import-job.model';
import { MiniCartPositions } from 'models/mini-cart';
import { OfferPreviewSettings } from 'models/offer-preview-settings';
import { OfferBase, OfferPricingState } from 'models/offer.model';
import { ProductSelector } from 'models/product-selector-item.model';
import { GroupedMouseoverText, ValueTextsStorage } from 'models/value-text.model';
import { LooseArticlesStorage } from 'models/loose-article.model';
import { OfferTableColumnsVisibility } from 'models/table.model';

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  constructor(
    private apollo: Apollo
  ) { }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setData(data: any, field: string): void {
    const oldData = this.apollo.client.readQuery({
      query: gql`query { ${field} @client}`
    });

    let newData;
    if (typeof data === 'object' && data !== null && JSON.stringify(data) !== '{}') {
      const oldDataField = oldData ? (oldData as any)[field] : {};

      if (Object.prototype.toString.call(data) === '[object Array]') {
        newData = [...oldDataField, ...data];
      } else {
        newData = { ...oldDataField, ...data };
      }
    } else {
      newData = data;
    }

    this.apollo.client.writeQuery({
      query: gql`query { ${field} @client}`,
      data: {
        [field]: newData
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  resetData(data: any, field: string): void {
    this.apollo.client.writeQuery({
      query: gql`query { ${field} @client}`,
      data: {
        [field]: data
      }
    });
  }

  getData(field: string): Observable<any> {
    return this.apollo.watchQuery({ query: gql`query { ${field} @client}` })
      .valueChanges.pipe(
        map((r) => (r.data as any)[field]),
        distinctUntilChanged()
      );
  }

  setCurrentOffer(data: OfferBase): void {
    this.setData(data, 'currentOffer');
  }

  getCurrentOffer(): Observable<OfferBase> {
    return this.getData('currentOffer');
  }

  setCurrentOfferMeineArticleIds(data: {meineArticlePositionIds: string[]}): void {
    this.setData(data, "currentOfferMeineArticleIds");
  }

  getCurrentOfferMeineArticleIds(): Observable<{meineArticlePositionIds: string[]}> {
    return this.getData("currentOfferMeineArticleIds");
  }

  resetCurrentOffer(): void {
    this.resetData({}, 'currentOffer');
  }

  setLastVisitedOffer(data: OfferBase): void {
    this.setData(data, 'lastVisitedOffer');
  }

  getLastVisitedOffer(): Observable<OfferBase> {
    return this.getData('lastVisitedOffer');
  }

  setOfferVersions(data: Offer[]): void {
    this.resetData(data, 'offerVersions');
  }

  getOfferVersions(): Observable<Offer[]> {
    return this.getData('offerVersions');
  }

  setProductSelector(data: ProductSelector | null): void {
    this.setData(data, 'productSelector');
  }

  getProductSelector(): Observable<ProductSelector> {
    return this.getData('productSelector');
  }

  setMiniCartPositions(data: MiniCartPositions): void {
    this.setData(data, 'miniCartPositions');
  }

  resetMiniCartPositions(): void {
    this.resetData({}, 'miniCartPositions');
  }

  getMiniCartPositions(): Observable<MiniCartPositions> {
    return this.getData('miniCartPositions');
  }

  resetCurrentDataItem(dataItemType?: DataResourceType): void {
    switch (dataItemType) {
      case 'Article':
        this.resetData({}, 'currentArticle');
        break;
      case 'Branch':
        this.resetData({}, 'currentBranch');
        break;
      case 'ContactPerson':
        this.resetData({}, 'currentContactPerson');
        break;
      case 'Customer':
        this.resetData({}, 'currentCustomer');
        break;
      case 'Object':
        this.resetData({}, 'currentObjekt');
        break;
      default:
        break;
    }
  }

  resetCurrentDataItems(): void {
    this.resetData({}, 'currentArticle');
    this.resetData({}, 'currentBranch');
    this.resetData({}, 'currentContactPerson');
    this.resetData({}, 'currentCustomer');
    this.resetData({}, 'currentObjekt');
  }

  setCurrentDataItem(data: DataItem): void {
    if (!data) { return; }
    // eslint-disable-next-line no-underscore-dangle
    switch (data.__typename) {
      case 'Object':
        this.setData(data, 'currentObjekt');
        break;
      case 'Customer':
        this.setData(data, 'currentCustomer');
        break;
      case 'ContactPerson':
        this.setData(data, 'currentContactPerson');
        break;
      case 'Article':
        this.setData(data, 'currentArticle');
        break;
      case 'Branch':
        this.setData(data, 'currentBranch');
        break;
      default:
        break;
    }
  }

  getCurrentArticle(): Observable<Article> {
    return this.getData('currentArticle');
  }

  getCurrentCustomer(): Observable<Customer> {
    return this.getData('currentCustomer');
  }

  getCurrentObjekt(): Observable<Objekt> {
    return this.getData('currentObjekt');
  }

  getOfferObject(): Observable<Objekt> {
    return this.getData('offerObject');
  }

  getCurrentContactPerson(): Observable<ContactPerson> {
    return this.getData('currentContactPerson');
  }

  setIsFirstContactPerson(value: boolean): void {
    this.setData(value, 'isFirstContactPerson');
  }

  getIsFirstContactPerson(): Observable<boolean> {
    return this.getData('isFirstContactPerson');
  }

  getCurrentBranch(): Observable<Branch> {
    return this.getData('currentBranch');
  }

  getBranches(): Observable<Branch[]> {
    return this.getData('branches');
  }

  setBranches(data: Branch[]): void {
    return this.setData(data, 'branches');
  }

  getCurrentDataItem(): Observable<DataItem> {
    return merge(
      this.getCurrentCustomer(),
      this.getCurrentContactPerson(),
      this.getCurrentObjekt()
    );
  }

  setFormReady(data: boolean): void {
    this.setData(data, 'formReady');
  }

  getFormReady(): Observable<boolean> {
    return this.getData('formReady');
  }

  setTableUpdate(data = true): void {
    this.setData(data, 'tableUpdate');
  }

  getTableUpdate(): Observable<boolean> {
    return this.getData('tableUpdate');
  }

  triggerTableUpdate(): Observable<boolean> {
    return this.getTableUpdate()
      .pipe(
        filter((value) => Boolean(value))
      );
  }

  setOfferUpdate(data: boolean | Partial<Offer> = true): void {
    this.setData(data, 'offerUpdate');
  }

  getOfferUpdate(): Observable<boolean | Partial<Offer>> {
    return this.getData('offerUpdate');
  }

  triggerOfferUpdate(): Observable<boolean | Partial<Offer>> {
    return this.getOfferUpdate()
      .pipe(
        filter((value) => Boolean(value))
      );
  }

  setAccount(data: Account): void {
    this.setData(data, 'currentAccount');
  }

  getAccount(): Observable<Account> {
    return this.getData('currentAccount');
  }

  getAccountId(): Observable<string> {
    return this.getAccount()
      .pipe(
        map((r) => r.id),
        distinctUntilChanged()
      );
  }

  getAccountOrganization(): Observable<Account['organization']> {
    return this.getAccount()
      .pipe(
        map((r) => r.organization),
        distinctUntilChanged()
      );
  }

  getAccountProfile(): Observable<AccountProfile> {
    return this.getAccount()
      .pipe(
        map((r) => r.profile as AccountProfile),
        distinctUntilChanged()
      );
  }

  getAccountProfileColumnsVisibility(): Observable<OfferTableColumnsVisibility> {
    return this.getAccount()
    .pipe(
      map((acc) => JSON.parse(acc.profile?.columnsVisibility || '[]')),
      distinctUntilChanged()
    );
  }

  getAccountProfileDisablePriceCalculation(): Observable<boolean> {
    return this.getAccount()
    .pipe(
      map((acc) => acc.profile?.disablePriceCalculation || false),
      distinctUntilChanged()
    );
  }

  getOfferDisablePriceCalculation(): Observable<boolean> {
    return this.getDisablePriceCalculation()
    .pipe(
      concatMap((data: boolean | null) => {
        if (data === null) {
          return this.getAccountProfileDisablePriceCalculation()
          .pipe(
            tap((data) => this.setDisablePriceCalculation(data))
          );
        }

        return of(data);
      })
    );
  }

  getDisablePriceCalculation(): Observable<boolean> {
    return this.getData( 'disablePriceCalculation');
  }

  setDisablePriceCalculation(data: boolean): void {
    this.resetData(data, 'disablePriceCalculation');
  }

  getOfferTableColumnsVisibility(): Observable<OfferTableColumnsVisibility> {
    return this.getOfferColumnsVisibility()
    .pipe(
      concatMap((colsSettings: OfferTableColumnsVisibility) => {
        if (!colsSettings.length) {
          return this.getAccountProfileColumnsVisibility()
          .pipe(
            tap((data) => this.setOfferColumnsVisibility(data))
          );
        }

        return of(colsSettings);
      })
    );
  }

  getOfferColumnsVisibility(): Observable<OfferTableColumnsVisibility> {
    return this.getData( 'offerColumnsVisibility');
  }

  setOfferColumnsVisibility(data: OfferTableColumnsVisibility): void {
    this.resetData(data, 'offerColumnsVisibility');
  }

  hasAccountProfile(): Observable<boolean> {
    return this.getAccountProfile()
      .pipe(
        map((acc) => Boolean(acc && acc.firstName && acc.lastName))
      );
  }

  setCurrentAccountBranch(data: Branch): void {
    this.setData(data, 'currentAccountBranch');
  }

  setCurrentObjekt(data: Objekt): void {
    this.setData(data, 'currentObjekt');
  }

  setOfferObject(data: Objekt | undefined | null): void {
    this.setData(data, 'offerObject');
  }

  getCurrentAccountBranch(): Observable<Branch> {
    return this.getData('currentAccountBranch');
  }

  getAccountManufacturer(): Observable<string> {
    return this.getAccount()
      .pipe(
        map((r) => r.manufacturer),
        distinctUntilChanged()
      );
  }

  isCurrentAccountDealer(): Observable<boolean> {
    return this.getAccount()
      .pipe(
        map((acc) => Boolean(acc && acc.accountType && acc.accountType === AccountTypeENUM.DEALER))
      );
  }

  getCurrentAccountRoles(): Observable<CurrentAccountRoles> {
    return combineLatest([
      this.getAccount(),
      this.getCurrentAccountBranch()
    ]).pipe(
      map(([acc, activeBranch]: [Account, Branch]) => {
        const activeBranchId = (activeBranch && activeBranch.id) || '';

        const roles: CurrentAccountRoles = {
          isOrgUser: false,
          isOrgAdmin: false,
          isOrgSuperAdmin: false,
          isBranchUser: false,
          isBranchAdmin: false,
          isBranchSuperAdmin: false,
          branchRoles: []
        };
        if (!(acc && acc.accountRoles)) return roles;

        if (acc.accountRoles.organizationRole) {
          const orgRole = acc.accountRoles.organizationRole;

          roles.isOrgUser = (orgRole === AccountRole.USER);
          roles.isOrgAdmin = (orgRole === AccountRole.ADMIN);
          roles.isOrgSuperAdmin = (orgRole === AccountRole.SUPERADMIN);
          roles.activeOrgRole = orgRole;
        }

        if (acc.accountRoles.branchRoles) {
          roles.branchRoles = acc.accountRoles.branchRoles;

          const branchRole = roles.branchRoles.find((b) => b.branchId === activeBranchId);
          const activeBranchRole = branchRole ? branchRole.role : null;

          roles.isBranchUser = (activeBranchRole === AccountRole.USER);
          roles.isBranchAdmin = (activeBranchRole === AccountRole.ADMIN);
          roles.isBranchSuperAdmin = (activeBranchRole === AccountRole.SUPERADMIN);
          roles.activeBranchRole = activeBranchRole as AccountRole;
        }

        return roles;
      })
    );
  }

  setOfferPreview(data: OfferPreviewSettings): void {
    this.setData(data, 'offerPreview');
  }

  getOfferPreview(): Observable<any> {
    return this.getData('offerPreview');
  }

  setCustomPosition(data: CustomLine): void {
    this.setData(data, 'customPosition');
  }

  resetCustomPosition(): void {
    this.resetData({}, 'customPosition');
  }

  getCustomPosition(): Observable<CustomLine> {
    return this.getData('customPosition');
  }

  addImportJob(id: string, type: ImportJobType, status: ImportJobStatus): Observable<void> {
    const importJobObj: ImportJob = { id, type, status };

    return this.getImportJob()
      .pipe(
        take(1),
        map((importJob: ImportJobs) => {
          const data = { ...importJob };
          data[type] = importJobObj;
          this.setImportJob(data);
        })
      );
  }

  removeImportJob(type: ImportJobType): Observable<boolean> {
    return this.getImportJob()
      .pipe(
        take(1),
        map((importJob: ImportJobs) => {
          const data = { ...importJob };
          delete data[type];

          this.setImportJob(data);

          return true;
        })
      );
  }

  setImportJob(data: ImportJobs): void {
    this.setData(data, 'importJob');
  }

  getImportJob(): Observable<ImportJobs> {
    return this.getData('importJob')
      .pipe(
        map((res) => {
          if (res === null) {
            this.resetData({}, 'importJob');

            return {};
          }

          return res;
        }),
        distinctUntilChanged()
      );
  }

  getImportJobByType(type: ImportJobType): Observable<ImportJob> {
    return this.getImportJob()
      .pipe(
        map((importJob: ImportJobs) => importJob[type] || {})
      );
  }

  setUpdatingPrices(data: boolean): void {
    this.setData(data, 'updatingPrices');
  }

  getUpdatingPrices(): Observable<boolean> {
    return this.getData('updatingPrices');
  }

  setCurrentPosition(data: ProductLine): void {
    this.setData(data, 'currentPosition');
  }

  getCurrentPosition(): Observable<ProductLine> {
    return this.getData('currentPosition');
  }

  setValueTexts(data: ValueTextsStorage): void {
    this.setData(data, 'valueTexts');
  }

  getValueTexts(): Observable<ValueTextsStorage> {
    return this.getData('valueTexts');
  }

  setMouseoverTexts(data: ValueTextsStorage): void {
    this.setData(data, 'mouseoverTexts');
  }

  getMouseoverTexts(): Observable<ValueTextsStorage> {
    return this.getData('mouseoverTexts');
  }

  setGroupedMouseoverTexts(data: GroupedMouseoverText): void {
    this.setData(data, 'groupedMouseoverTexts');
  }

  getGroupedMouseoverTexts(): Observable<GroupedMouseoverText> {
    return this.getData('groupedMouseoverTexts');
  }

  getLooseArticles(): Observable<LooseArticlesStorage> {
    return this.getData('looseArticles');
  }

  setLooseArticles(data: LooseArticlesStorage): void {
    this.setData(data, 'looseArticles');
  }

  resetLooseArticles(): void {
    this.resetData({}, 'looseArticles');
  }

  setCurrentSftp(data: SftpCredentialsResult): void {
    this.setData(data, 'currentSftp');
  }

  getCurrentSftp(): Observable<SftpCredentialsResult> {
    return this.getData('currentSftp');
  }

  resetCurrentSftp(): void {
    this.resetData(null, 'currentSftp');
  }

  accountHasTestView(): Observable<boolean> {
    const isDev = isDevEnv();

    return combineLatest([
      this.getAccount(),
      this.getCurrentAccountRoles()
    ]).pipe(
      map(([account, roles]: [Account, CurrentAccountRoles]) => {
        const username = account.username;
        const isSuperAdmin = roles.isOrgSuperAdmin || roles.isBranchSuperAdmin;

        return isDev && isSuperAdmin && ALLOW_TEST_VIEW.includes(username)
      })
    );
  }

  isSuperAdmin(): Observable<boolean> {
    return combineLatest([
      this.getCurrentAccountRoles()
    ]).pipe(
      map(([roles]: [CurrentAccountRoles]) => {
        return roles.isOrgSuperAdmin || roles.isBranchSuperAdmin
      })
    );
  }

  getCurrentOfferPricingState(): Observable<OfferPricingState> {
    return this.getCurrentOffer()
    .pipe(
      map((offer) => ({
        autoUpdatePrices: offer.autoUpdatePrices,
        priceIsOutdated: offer.priceIsOutdated
      }))
    );
  }
}
